'use client'

import { useEffect, useState } from 'react'

// Wrapper Component
export function FormField({
  name,
  label,
  required = false,
  description,
  classNames,
  children,
  error,
}: {
  name: string
  label: string
  required?: boolean
  description?: string
  classNames?: {
    field?: string
    input?: string
    choice?: string
  }
  children: React.ReactNode
  error?: string
}) {
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    setHasError(error && error.length > 0)
  }, [error])

  const fieldClassName = classNames?.field || ''

  return (
    <div
      className={`${fieldClassName} flex flex-col gap-xxs ${hasError ? 'has-error' : ''}`}
      onChange={() => setHasError(false)}>
      <div className="flex flex-col gap-xxxs">
        <label htmlFor={`field-${name}`}>
          {label} {required ? '*' : ''}
        </label>

        {description && <div className="text-[length:--p-s-size]">{description}</div>}
      </div>

      {children}

      {error && <div className="error-msg">{error}</div>}
    </div>
  )
}

// Text Input Component
export function TextInput({
  name,
  placeholder = '',
  defaultValue = '',
  required = false,
  className,
}: {
  name: string
  placeholder?: string
  defaultValue?: string
  required?: boolean
  className?: string
}) {
  return (
    <input
      id={`field-${name}`}
      placeholder={placeholder}
      name={name}
      type="text"
      defaultValue={defaultValue}
      className={className}
      required={required}
    />
  )
}

// Email Input Component
export function EmailInput({
  name,
  placeholder = '',
  defaultValue = '',
  required = false,
  className,
}: {
  name: string
  placeholder?: string
  defaultValue?: string
  required?: boolean
  className?: string
}) {
  return (
    <input
      id={`field-${name}`}
      placeholder={placeholder}
      name={name}
      type="email"
      defaultValue={defaultValue}
      className={className}
      required={required}
    />
  )
}

// Select Input Component
// TODO: I can't for the life of me get defaultValue to work with the select input - it always defaults to the first option on save
export function SelectInput({
  name,
  defaultValue = '',
  choices,
  required = false,
  className,
}: {
  name: string
  defaultValue?: string | number
  choices: { label: string; value: string }[] | string[]
  required?: boolean
  className?: string
}) {
  const [selectedValue, setSelectedValue] = useState<string | number>(defaultValue)
  const [opened, setOpened] = useState(false)

  // Ensure choices are in the correct format
  const formattedChoices = choices.map((choice) => {
    if (typeof choice === 'string') {
      return { label: choice, value: choice }
    }
    return choice
  })

  // Put the selected value in the first position
  formattedChoices.sort((a, b) => {
    if (a.value === selectedValue) {
      return -1
    }
    if (b.value === selectedValue) {
      return 1
    }
    return 0
  })

  return (
    <select
      id={`field-${name}`}
      name={name}
      className={className}
      required={required}
      defaultValue={selectedValue}
      onChange={(e) => {
        setSelectedValue(e.target.value)
      }}
      onFocus={() => setOpened(true)}
      onBlur={() => setOpened(false)}>
      {!selectedValue && required ? (
        <option value="" disabled={opened}>
          Select an option
        </option>
      ) : null}

      {formattedChoices.map((choice: { label: string; value: string }) => {
        const { label, value } = choice

        return (
          <option key={value} value={value}>
            {label}
          </option>
        )
      })}
    </select>
  )
}

// Checkbox Input Component
export function CheckboxInput({
  name,
  defaultValue = [],
  choices,
  classNames,
}: {
  name: string
  defaultValue?: string[]
  choices: { label: string; value: string }[] | string[]
  classNames?: {
    input?: string
    choice?: string
  }
}) {
  return (
    <div className={classNames?.input}>
      {choices.map((choice) => {
        const { label, value } =
          typeof choice === 'string' ? { label: choice, value: choice } : choice

        const isChecked = Array.isArray(defaultValue) && defaultValue.some((item) => item == value)

        return (
          <label key={value} className={`flex items-center gap-xxs ${classNames?.choice}`}>
            <input type="checkbox" name={`${name}[]`} value={value} defaultChecked={isChecked} />
            {label}
          </label>
        )
      })}
    </div>
  )
}

// Radio Input Component
export function RadioInput({
  name,
  defaultValue = '',
  choices,
  className,
}: {
  name: string
  defaultValue?: string
  choices: { label: string; value: string }[] | string[]
  className?: string
}) {
  return (
    <div className={className}>
      {choices.map((choice) => {
        const { label, value } =
          typeof choice === 'string' ? { label: choice, value: choice } : choice

        return (
          <label key={value} className="flex items-center gap-8">
            <input type="radio" name={name} value={value} defaultChecked={defaultValue == value} />
            {label}
          </label>
        )
      })}
    </div>
  )
}

// Textarea Input Component
export function TextareaInput({
  name,
  placeholder = '',
  required = false,
  defaultValue = '',
  className,
}: {
  name: string
  placeholder?: string
  required?: boolean
  defaultValue?: string
  className?: string
}) {
  return (
    <textarea
      id={`field-${name}`}
      required={required}
      placeholder={placeholder}
      name={name}
      defaultValue={defaultValue}
      className={className}
    />
  )
}
